import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { buildWhatsAppLink } from "../lib/site";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = [
  "₹15 - 25 Lakh",
  "₹25 - 50 Lakh",
  "₹50 Lakh - 1 Cr",
  "₹1 - 3 Cr",
  "Above ₹3 Cr",
];
const INTERESTS = [
  "Dholera Residential Plot",
  "Commercial Property",
  "Agricultural / Land Parcel",
  "Industrial Land",
  "Investment Advisory",
];

export default function LeadForm({
  variant = "inline",
  defaultMessage = "",
  onSuccess,
  testidPrefix = "lead",
}) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    budget: "",
    property_interest: "",
    site_visit_date: "",
    message: defaultMessage,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        email: form.email || null,
        source: variant,
      });
      toast.success(
        "Thank you! Our advisor will reach out to you shortly."
      );
      const waMsg = `Hello, I'm ${form.name}. I'm interested in ${
        form.property_interest || "Dholera investment"
      }. Budget: ${form.budget || "to discuss"}. Please share details.`;
      window.open(buildWhatsAppLink(waMsg), "_blank");
      setForm({
        name: "",
        phone: "",
        email: "",
        budget: "",
        property_interest: "",
        site_visit_date: "",
        message: "",
      });
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/15 text-white py-3 px-0 focus:border-[#D4AF37] outline-none placeholder:text-white/30 transition-colors text-sm font-light";

  return (
    <form
      onSubmit={submit}
      data-testid={`${testidPrefix}-form`}
      className="grid md:grid-cols-2 gap-x-8 gap-y-6"
    >
      <div className="flex flex-col">
        <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">
          Name *
        </label>
        <input
          data-testid={`${testidPrefix}-name`}
          type="text"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Your full name"
          className={inputCls}
          required
        />
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">
          Phone *
        </label>
        <input
          data-testid={`${testidPrefix}-phone`}
          type="tel"
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          placeholder="+91 ..."
          className={inputCls}
          required
        />
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">
          Email
        </label>
        <input
          data-testid={`${testidPrefix}-email`}
          type="email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="you@example.com"
          className={inputCls}
        />
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">
          Budget
        </label>
        <select
          data-testid={`${testidPrefix}-budget`}
          value={form.budget}
          onChange={(e) => update("budget", e.target.value)}
          className={`${inputCls} appearance-none cursor-pointer`}
        >
          <option value="" className="bg-black">
            Select budget
          </option>
          {BUDGETS.map((b) => (
            <option key={b} value={b} className="bg-black">
              {b}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">
          Property Interest
        </label>
        <select
          data-testid={`${testidPrefix}-interest`}
          value={form.property_interest}
          onChange={(e) => update("property_interest", e.target.value)}
          className={`${inputCls} appearance-none cursor-pointer`}
        >
          <option value="" className="bg-black">
            Select interest
          </option>
          {INTERESTS.map((b) => (
            <option key={b} value={b} className="bg-black">
              {b}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">
          Preferred Site Visit Date
        </label>
        <input
          data-testid={`${testidPrefix}-date`}
          type="date"
          value={form.site_visit_date}
          onChange={(e) => update("site_visit_date", e.target.value)}
          className={`${inputCls} [color-scheme:dark]`}
        />
      </div>
      <div className="flex flex-col md:col-span-2">
        <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">
          Message
        </label>
        <textarea
          data-testid={`${testidPrefix}-message`}
          rows={3}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Tell us about your investment goals..."
          className={`${inputCls} resize-none`}
        />
      </div>
      <div className="md:col-span-2 mt-4">
        <button
          type="submit"
          disabled={loading}
          data-testid={`${testidPrefix}-submit`}
          className="group relative inline-flex items-center justify-center bg-[#D4AF37] text-black font-medium tracking-[0.2em] uppercase text-xs px-10 py-4 hover:bg-[#B3932E] transition-all duration-300 shadow-[0_0_25px_rgba(212,175,55,0.25)] hover:shadow-[0_0_45px_rgba(212,175,55,0.5)] disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending
            </>
          ) : (
            "Book Free Site Visit →"
          )}
        </button>
        <p className="mt-4 text-[11px] tracking-wider text-white/40">
          By submitting, a WhatsApp chat will also open with our advisor.
        </p>
      </div>
    </form>
  );
}
